/**
 * ThinkingIndicator
 *
 * The assistant "working" indicator shown while a reasoning task runs: the nav
 * Ahoy logo inside a brand-colour loader ring beside an italic brand label.
 *
 *  - Before any reasoning step streams in, the label is the live backend
 *    status, else "Thinking…".
 *  - Once steps stream in, the label is the most recent step's title, and a
 *    chevron beside it expands the full reasoning steps list below. The list
 *    starts collapsed on every run; the label is the live progress cue.
 *  - Each label is held for a minimum time before the next replaces it, so
 *    steps that finish in a blink don't make the label flicker.
 *
 * The loader row keeps one position in the tree across both phases, so the
 * spinner never remounts (and restarts) when the first step arrives.
 */
import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { useMinimumDisplayTime } from '@/lib/hooks/useMinimumDisplayTime';

import { InlineReasoningCard } from './InlineReasoningCard';
import { INLINE_TITLE_MARKDOWN_COMPONENTS } from './inlineStepTitle';
import { resolveLatestStepTitle } from './latestReasoningStep';
import type { ReasoningStepData } from './ReasoningStepsList';

/** Minimum time a loader label stays up before the next one replaces it. */
const LABEL_MIN_DISPLAY_MS = 1500;

const LABEL_CLASS =
  'min-w-0 truncate text-[14px] font-medium italic leading-[20px] text-fg-brand-primary';

interface ThinkingIndicatorProps {
  /** Live status text from polling; an empty string means "no progress yet". */
  progressStatus: string;
  /** In-flight reasoning steps streamed from the backend. */
  partialSteps: ReasoningStepData[];
  /** Chain id for the steps list's expanded-state scoping (optional). */
  chainId?: string;
  /** Callback when a dynamic view is opened from a reasoning step. */
  onOpenDynamicViewSidebar?: (viewId: number) => void;
}

export function ThinkingIndicator({
  progressStatus,
  partialSteps,
  chainId,
  onOpenDynamicViewSidebar,
}: ThinkingIndicatorProps) {
  const latestStepTitle = resolveLatestStepTitle(partialSteps);
  const hasSteps = latestStepTitle !== null;
  // Steps can complete faster than a label can be read; hold each label on
  // screen for a moment so the row advances steadily instead of flickering.
  const label = useMinimumDisplayTime(
    latestStepTitle ?? (progressStatus || 'Thinking…'),
    LABEL_MIN_DISPLAY_MS
  );

  return (
    <AccordionItem>
      <div className='flex items-center gap-2'>
        <div
          role='status'
          aria-live='polite'
          className='flex min-w-0 items-center gap-3'
        >
          <LoadingIndicator type='ahoy' size='md' inline />
          {/* Step titles can carry <entity> tags and light markdown, so the
              label renders through the same inline markdown the steps list
              uses for its titles. */}
          <MarkdownContent
            content={label}
            size='compact'
            as='span'
            entityInteractive={false}
            components={INLINE_TITLE_MARKDOWN_COMPONENTS}
            className={LABEL_CLASS}
          />
        </div>
        {hasSteps && (
          <AccordionTrigger
            aria-label='Reasoning steps'
            className='shrink-0 rounded-md p-1 transition duration-100 ease-linear hover:bg-primary_hover'
          >
            <AccordionChevron strokeWidth={1.5} />
          </AccordionTrigger>
        )}
      </div>

      {hasSteps && (
        // The indent aligns the steps under the label: the ahoy `md` loader
        // (48px) + gap-3 puts the label at x=60, while a step's own 24px icon
        // + gap-3 puts its title at x=36; pl-6 closes that 24px gap.
        <AccordionPanel className='pl-6 pt-3'>
          <InlineReasoningCard
            statusText={progressStatus}
            isProcessing
            steps={partialSteps}
            chainId={chainId}
            onOpenDynamicViewSidebar={onOpenDynamicViewSidebar}
          />
        </AccordionPanel>
      )}
    </AccordionItem>
  );
}

export default ThinkingIndicator;
